<script setup lang="ts">
import type { PositionColumn, PositionRow, PredefinedPosition } from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import { idOf, usePersistedRows } from '@/composables/usePersistedRows'
import { blankPositionRow, freePositionPayload, isPositionDraft } from '@/pages/investors/credit/creditPositions'
import { expenditureSuggestions } from '@/pages/investors/credit/expenditureSuggestions'
import { liabilitySuggestions } from '@/pages/investors/credit/liabilitySuggestions'
import type { PositionCategory } from '@/pages/investors/credit/types'
import { requestJson } from '@/utils/postJson'
import ExpenditurePositionController from '@controllers/Investors/ExpenditurePositionController'
import LiabilityPositionController from '@controllers/Investors/LiabilityPositionController'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import FreePositionData = App.Data.SelfDisclosure.FreePositionData

const props = defineProps<{
  category: PositionCategory
  personId: string
  serverRows: PositionRow[]
}>()

const { t } = useI18n({ useScope: 'local' })

const controllers = {
  expenses: ExpenditurePositionController,
  liabilities: LiabilityPositionController,
}

const positionRoute = (id: string): [string, string] => [props.personId, id]

const { rows, reset, addDraft, removeRow, persistOnLeave } = usePersistedRows({
  toPayload: freePositionPayload,
  isDraft: isPositionDraft,
  create: async (payload) =>
    idOf(await requestJson<FreePositionData>('post', controllers[props.category].store(props.personId).url, payload)),
  update: (id, payload) => requestJson('put', controllers[props.category].update(positionRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', controllers[props.category].destroy(positionRoute(id)).url, undefined),
  reloadProps: ['people', 'creditCompletion'],
})

reset(props.serverRows)

watch(() => props.serverRows, reset)

const names: Record<PositionCategory, string[]> = {
  expenses: expenditureSuggestions,
  liabilities: liabilitySuggestions,
}

const suggestions = computed<PredefinedPosition[]>(() =>
  names[props.category].map((name) => ({ key: name, label: name })),
)

const columns = computed<PositionColumn[]>(() => [
  {
    key: 'label',
    label: t(`${props.category}.type`),
    type: 'suggest',
    suggestions: suggestions.value,
    placeholder: t('namePlaceholder'),
  },
  { key: 'amount', label: t(`${props.category}.amount`), type: 'currency', fractionDigits: 0, total: true },
])

const accents: Record<PositionCategory, string> = {
  expenses: 'bg-brand-blue-2',
  liabilities: 'bg-brand-teal',
}
</script>

<template>
  <section class="bg-surface-2! fh-card shadow-none!" @focusout="persistOnLeave">
    <header class="flex items-start gap-3 px-6 py-4">
      <span class="mt-1 h-8 w-1 flex-none rounded-full" :class="accents[category]" />
      <div>
        <h3 class="text-base font-bold">{{ t(`groups.${category}`) }}</h3>
        <p class="text-ink-3 text-sm">{{ t(`hints.${category}`) }}</p>
      </div>
    </header>

    <div class="py-5">
      <FinestCustomPositionListCard
        v-model:rows="rows"
        plain
        flush
        title=""
        :columns="columns"
        editable-rows
        @add-row="addDraft(blankPositionRow())"
        @remove-row="removeRow" />
    </div>
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "expenses": {
      "type": "Ausgabenart",
      "amount": "Betrag / Jahr"
    },
    "liabilities": {
      "type": "Verbindlichkeit",
      "amount": "Betrag"
    },
    "groups": {
      "expenses": "Ausgaben",
      "liabilities": "Verbindlichkeiten"
    },
    "hints": {
      "expenses": "Regelmäßige Ausgaben deines Haushalts – jeweils als Jahresbetrag.",
      "liabilities": "Laufende Kredite und sonstige Verbindlichkeiten mit dem offenen Betrag."
    },
    "namePlaceholder": "Bezeichnung"
  },
  "en": {
    "expenses": {
      "type": "Expense type",
      "amount": "Amount / year"
    },
    "liabilities": {
      "type": "Liability",
      "amount": "Amount"
    },
    "groups": {
      "expenses": "Expenses",
      "liabilities": "Liabilities"
    },
    "hints": {
      "expenses": "Recurring expenses of your household – as a yearly amount each.",
      "liabilities": "Current loans and other liabilities with their outstanding amount."
    },
    "namePlaceholder": "Name"
  }
}
</i18n>
